// ModelMultiSelect.jsx — Combobox dropdown for selecting available models.
//
// Replaces the free-form textarea in AdapterSheet. Pre-populated list from
// adapterPresets per provider + free-form "add custom" when the user needs
// a model not in the catalog. Sprint v8.3.
//
// Design: shad-pro popover pattern + ui-ux-pro-max keyboard nav + impeccable
// micro-interactions. Selected models render as removable chips below the
// trigger; click trigger opens panel with checkbox list + search + custom-add.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronDown, Loader2, Plus, Search, Sparkles, X } from 'lucide-react';
import { getModelOptions } from '../../../lib/adapterPresets.js';
import { getLiveModelLoader } from '../../../lib/openrouterModels.js';

export default function ModelMultiSelect({ adapterName, value = [], onChange, defaultModel, onSuggest, suggestBusy = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('');
  // Catálogo ao vivo (ex: OpenRouter expõe 350+ modelos num endpoint público).
  // idle → loading → loaded | error. Buscamos só quando o painel abre.
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [remoteState, setRemoteState] = useState('idle');
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const customInputRef = useRef(null);
  // Garante UMA busca por adapter (não por render) + setState seguro pós-unmount.
  const loadStartedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Pre-defined model options for this adapter (e.g. deepseek-chat,
  // deepseek-reasoner). Anything the user typed manually still appears
  // as a selected chip even if it's not in the catalog.
  const presetOptions = useMemo(() => getModelOptions(adapterName), [adapterName]);

  // Loader de catálogo ao vivo do adapter (hoje: só OpenRouter). null → só preset.
  const liveLoader = useMemo(() => getLiveModelLoader(adapterName), [adapterName]);

  // Dispara a busca do catálogo. Falha é não-fatal: marca 'error' e seguimos
  // com os presets (já presentes no union abaixo). NÃO depende de remoteState,
  // então setar 'loading' aqui não re-dispara o effect e descarta o resultado.
  const loadRemote = useCallback(
    (opts) => {
      if (!liveLoader) return;
      setRemoteState('loading');
      liveLoader(opts)
        .then((models) => {
          if (!mountedRef.current) return;
          setRemoteOptions(Array.isArray(models) ? models : []);
          setRemoteState('loaded');
        })
        .catch(() => {
          if (mountedRef.current) setRemoteState('error');
        });
    },
    [liveLoader],
  );

  // Reseta o catálogo ao vivo quando o adapter muda (sheet reaproveitado).
  useEffect(() => {
    loadStartedRef.current = false;
    setRemoteOptions([]);
    setRemoteState('idle');
  }, [adapterName]);

  // Busca o catálogo ao vivo na primeira abertura do painel (uma vez por adapter).
  useEffect(() => {
    if (!open || !liveLoader || loadStartedRef.current) return;
    loadStartedRef.current = true;
    loadRemote();
  }, [open, liveLoader, loadRemote]);

  // Union: preset + catálogo ao vivo + modelos já selecionados (custom),
  // deduplicado. Com catálogo ao vivo (354 itens) ordenamos alfabético pra
  // navegabilidade; sem ele preservamos a ordem curada do preset.
  const allOptions = useMemo(() => {
    const arr = Array.from(new Set([...presetOptions, ...remoteOptions, ...value]));
    if (remoteOptions.length > 0) arr.sort((a, b) => a.localeCompare(b));
    return arr;
  }, [presetOptions, remoteOptions, value]);

  const filtered = useMemo(() => {
    if (!search.trim()) return allOptions;
    const q = search.toLowerCase();
    return allOptions.filter((m) => m.toLowerCase().includes(q));
  }, [allOptions, search]);

  // Outside click + Esc close.
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
        setCustomMode(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        setCustomMode(false);
      }
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (customMode && customInputRef.current) customInputRef.current.focus();
  }, [customMode]);

  function toggle(model) {
    if (value.includes(model)) {
      onChange(value.filter((m) => m !== model));
    } else {
      onChange([...value, model]);
    }
  }

  function commitCustom() {
    const v = customValue.trim();
    if (!v) {
      setCustomMode(false);
      return;
    }
    if (!value.includes(v)) onChange([...value, v]);
    setCustomValue('');
    setCustomMode(false);
  }

  // Seleciona tudo que está VISÍVEL (respeita o filtro). Com 354 modelos,
  // "todos" sem filtro selecionaria a lista inteira — buscar antes ("claude")
  // e clicar "todos" vira o atalho útil pra marcar um subconjunto.
  function selectAll() {
    onChange(Array.from(new Set([...value, ...filtered])));
  }

  function retryRemote() {
    loadRemote({ force: true });
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between text-apolo-textSecondary text-sm">
        <span>Modelos disponíveis</span>
        {onSuggest && (
          <button
            type="button"
            onClick={onSuggest}
            disabled={suggestBusy}
            title="Busca os modelos vivos do provider e adiciona os novos"
            className="inline-flex items-center gap-1 rounded px-1 text-[10px] text-apolo-accent transition hover:text-apolo-accentHover focus:outline-none focus:ring-2 focus:ring-apolo-accent/40 disabled:opacity-50"
          >
            <Sparkles size={10} aria-hidden="true" />
            {suggestBusy ? 'Buscando…' : 'Sugerir modelos'}
          </button>
        )}
      </span>

      {/* Trigger button */}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={
            'flex w-full items-center justify-between gap-2 rounded-md border bg-apolo-surface2 px-3 py-2 text-left text-xs transition focus:outline-none focus:ring-2 focus:ring-apolo-accent/40 ' +
            (open
              ? 'border-apolo-accent text-apolo-textPrimary'
              : 'border-apolo-border text-apolo-textSecondary hover:border-apolo-accent/50')
          }
        >
          <span className="truncate">
            {value.length === 0
              ? 'Selecionar modelos…'
              : value.length === 1
                ? value[0]
                : `${value.length} modelos selecionados`}
          </span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={'shrink-0 transition-transform ' + (open ? 'rotate-180' : '')}
          />
        </button>

        {/* Popover panel */}
        {open && (
          <div
            ref={panelRef}
            role="listbox"
            aria-label="Modelos disponíveis"
            aria-multiselectable="true"
            className="absolute left-0 right-0 z-30 mt-1 flex max-h-[280px] flex-col overflow-hidden rounded-md border border-apolo-border bg-apolo-surface1 shadow-[0_8px_24px_rgba(0,0,0,0.4)] ring-1 ring-apolo-accent/10"
          >
            {/* Search */}
            <div className="flex items-center gap-1.5 border-b border-apolo-borderSubtle px-2 py-1.5">
              <Search
                size={11}
                className="text-apolo-textTertiary"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrar…"
                aria-label="Filtrar modelos"
                className="flex-1 bg-transparent text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
              />
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded px-1.5 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textSecondary"
                >
                  limpar
                </button>
              )}
              {allOptions.length > 0 && (
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded px-1.5 text-[10px] text-apolo-accent transition hover:text-apolo-accentHover"
                  title={search.trim() ? 'Selecionar os modelos filtrados' : 'Selecionar todos'}
                >
                  todos
                </button>
              )}
            </div>

            {/* Status do catálogo ao vivo (ex: OpenRouter) — loading/contagem/erro */}
            {liveLoader && (
              <div className="flex items-center gap-1.5 border-b border-apolo-borderSubtle px-2.5 py-1 text-[10px] text-apolo-textTertiary">
                {remoteState === 'loading' && (
                  <>
                    <Loader2 size={10} className="animate-spin" aria-hidden="true" />
                    <span>Carregando catálogo completo…</span>
                  </>
                )}
                {remoteState === 'loaded' && (
                  <span aria-live="polite">
                    {allOptions.length} modelos disponíveis
                    {value.length > 0 && ` · ${value.length} selecionados`}
                  </span>
                )}
                {remoteState === 'error' && (
                  <>
                    <AlertCircle size={10} className="text-apolo-error" aria-hidden="true" />
                    <span className="text-apolo-error">Falha ao carregar o catálogo.</span>
                    <button
                      type="button"
                      onClick={retryRemote}
                      className="rounded px-1 text-apolo-accent transition hover:text-apolo-accentHover"
                    >
                      tentar de novo
                    </button>
                  </>
                )}
              </div>
            )}

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 && !customMode && remoteState !== 'loading' && (
                <p className="px-3 py-4 text-center text-[11px] text-apolo-textTertiary">
                  Nenhum modelo encontrado. Adicione um customizado abaixo.
                </p>
              )}
              {filtered.map((model) => {
                const checked = value.includes(model);
                const isDefault = model === defaultModel;
                return (
                  <button
                    key={model}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(model)}
                    className={
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs transition ' +
                      (checked
                        ? 'bg-apolo-accentMuted text-apolo-textPrimary'
                        : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary')
                    }
                  >
                    <span
                      aria-hidden="true"
                      className={
                        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ' +
                        (checked
                          ? 'border-apolo-accent bg-apolo-accent text-apolo-bg'
                          : 'border-apolo-border bg-apolo-surface2')
                      }
                    >
                      {checked && <Check size={9} strokeWidth={3} aria-hidden="true" />}
                    </span>
                    <span className="flex-1 truncate font-mono">{model}</span>
                    {isDefault && (
                      <span className="rounded bg-apolo-highlightMuted px-1 text-[9px] uppercase tracking-wide text-apolo-highlight">
                        default
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Custom add row */}
            <div className="border-t border-apolo-borderSubtle p-1.5">
              {customMode ? (
                <div className="flex items-center gap-1 rounded bg-apolo-surface2 px-2 py-1">
                  <input
                    ref={customInputRef}
                    type="text"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitCustom();
                      } else if (e.key === 'Escape') {
                        setCustomMode(false);
                        setCustomValue('');
                      }
                    }}
                    onBlur={commitCustom}
                    placeholder="nome-do-modelo"
                    className="flex-1 bg-transparent font-mono text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={commitCustom}
                    className="rounded bg-apolo-accent px-2 py-0.5 text-[10px] font-medium text-apolo-bg transition hover:bg-apolo-accent/90"
                  >
                    Adicionar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCustomMode(true)}
                  className="flex w-full items-center justify-center gap-1 rounded px-2 py-1 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
                >
                  <Plus size={11} aria-hidden="true" />
                  Adicionar modelo customizado
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected chips below */}
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 pt-1" aria-label="Modelos selecionados">
          {value.map((m) => (
            <li key={m}>
              <span
                className={
                  'inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] ' +
                  (m === defaultModel
                    ? 'border-apolo-highlightRing bg-apolo-highlightMuted text-apolo-textPrimary'
                    : 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary')
                }
              >
                {m}
                <button
                  type="button"
                  onClick={() => toggle(m)}
                  aria-label={`Remover ${m}`}
                  className="rounded text-apolo-textTertiary transition hover:text-apolo-error"
                >
                  <X size={9} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
